// Стартовый экран по роли.
//
// Роль определяет ЭКРАН, а не URL входа: вход единый `/login` (app.md §6).
// Разводить входы по ролям нельзя — тогда человек, которому сменили роль, стучится
// в старый адрес и получает отказ без объяснения.

import { getViewMode } from '@/shared/storage';

import type { CurrentUser } from './api';

export function startScreenFor(user: CurrentUser): string {
  // Режим отображения побеждает роль ВНУТРИ сессии, но не при входе.
  //
  // Прежнее правило звучало так: моноблок настроен показывать киоск, значит подошедший
  // к нему обязан увидеть кухонный экран. Оно не учитывало, что режим переживает выход
  // и достаётся СЛЕДУЮЩЕМУ вошедшему: повар поработал на устройстве — и менеджер,
  // войдя после него, приземлялся в киоск вместо своего дашборда. Настройка устройства
  // подменяла собой роль человека.
  //
  // Теперь режим выставляется при входе по роли (`LoginPage`), а эта проверка отвечает
  // только за переходы внутри уже начатой сессии: менеджер, переключившийся в киоск
  // и попавший на корень, обязан остаться в киоске, а не выпасть в кабинет.
  if (getViewMode() === 'kiosk') {
    return '/kiosk/print';
  }

  // Не прошедший первый вход уйдёт в визард своей роли (T-005). Отдельной ветки
  // здесь НЕТ намеренно: пока визардов нет, она вернула бы то же самое, а мёртвая
  // развилка читается как рабочая и переживает не одну правку.
  //
  // Раньше она существовала и возвращала `'/'` — то есть редирект с корня на корень:
  // `StartPage` на `'/'` рендерит `<Navigate>` на `startScreenFor(...)`, который снова
  // отдавал `'/'`. Пустой экран без единого объяснения, и не у сотрудника, а у ЛЮБОЙ
  // роли при первом входе (риск Р9).
  return startScreenForRole(user.role);
}

function startScreenForRole(role: CurrentUser['role']): string {
  switch (role) {
    case 'platform_admin':
      // Свой раздел платформы (T-038): аналитика по всем сетям. Раньше здесь стоял
      // каталог сети — «ближайший осмысленный» за отсутствием своих экранов.
      return '/platform';
    case 'client_admin':
      return '/monitoring';
    case 'location_manager':
      // Дашборд, а не меню: `expiry-monitoring/story-01` называет его стартовым экраном
      // менеджера точки. Меню он открывает раз в неделю, а «что горит сейчас» — каждую
      // смену, и приземление не на тот экран стоит менеджеру лишнего перехода
      // при каждом входе.
      return '/monitoring';
    case 'employee':
      // Печать — центральный поток продукта и единственное, ради чего повар открывает
      // приложение. Раньше эта ветка отсутствовала, и `default` возвращал `'/'` — то есть
      // РЕДИРЕКТ С КОРНЯ НА КОРЕНЬ: `StartPage` на `'/'` рендерит `<Navigate>` на
      // `startScreenFor(...)`, который снова отдавал `'/'`. Пустой экран без объяснения,
      // и поймать это можно было только руками (риск Р9).
      return '/kiosk/print';
    default:
      // Ветка недостижима: роли перечислены исчерпывающе. Оставлена как страховка
      // на случай новой роли — и возвращает ОСМЫСЛЕННЫЙ экран, а не `'/'`.
      return '/monitoring';
  }
}
